import { Inject, Injectable } from '@angular/core';
import { Action, Selector, State, StateContext } from '@ngxs/store';
import { MAP_CONFIG, MapConfig } from '@x/common/map/map';
import { ChannelSetActive } from '@x/ecommerce-shop/app/channel/state';
import { CheckoutSetOrder } from '@x/ecommerce-shop/app/checkout/state/checkout-order.actions';
import { AddressService } from '@x/ecommerce-shop/app/core/address/services/address.service';
import { OrderSetShopOrder } from '@x/ecommerce-shop/app/core/order/state/shop-order.actions';
import {
  RegionCloseOverlay,
  RegionOpenOverlay,
  RegionSet,
  RegionSetFromAddressInput,
  RegionUpdateCoordinates,
} from '@x/ecommerce-shop/app/core/region/state/region.actions';
import { IShopConfig, SHOP_CONFIG } from '@x/ecommerce-shop/config/shop.config';
import {
  IShopAddressInput,
  IShopGeoRegionDetail,
  IShopProductGeoRegion,
  ShopRegionService,
} from '@x/ecommerce/shop-client';
import { PointInput } from '@x/schemas/ecommerce';
import { tap } from 'rxjs/operators';

export interface IProductContext {
  id: number;
  variantId: number;
  quantity: number;
  variantAvailableRegions: IShopProductGeoRegion[];
}

export interface IRegionStateModel {
  addressInput: IShopAddressInput | null;
  channelRegions: IShopGeoRegionDetail[];
  region: IShopGeoRegionDetail | null;
  mode: 'normal' | 'add-to-cart';
  productContext: IProductContext | null;
  deliveryPolygons: google.maps.PolygonOptions[];
  deliveryMarkers: google.maps.MarkerOptions[];
  defaultCoordinates: PointInput | null;
  openOverlay: boolean;
}

const defaults: IRegionStateModel = {
  addressInput: null,
  channelRegions: [],
  region: null,
  mode: 'normal',
  deliveryPolygons: [],
  deliveryMarkers: [],
  defaultCoordinates: null,
  openOverlay: false,
  productContext: null,
};

@State<IRegionStateModel>({
  name: 'regionState',
  defaults,
})
@Injectable()
export class RegionState {
  @Selector() static addressInput(state: IRegionStateModel): IShopAddressInput | null {
    return state.addressInput;
  }

  @Selector() static openOverlay(state: IRegionStateModel): boolean {
    return state.openOverlay;
  }

  constructor(
    @Inject(SHOP_CONFIG) private shopConfig: IShopConfig,
    private shopRegionService: ShopRegionService,
    @Inject(MAP_CONFIG)
    private mapThemeConfig: MapConfig,
    private addressService: AddressService,
  ) {}

  @Action(RegionSet)
  set({ patchState }: StateContext<IRegionStateModel>, { region }: RegionSet) {
    patchState({
      region,
      deliveryMarkers: this.getGoogleMarkers(region),
      deliveryPolygons: this.getGooglePolygons(region),
    });
  }

  @Action(CheckoutSetOrder)
  @Action(OrderSetShopOrder)
  setFromOrder(
    { dispatch, patchState }: StateContext<IRegionStateModel>,
    { order }: OrderSetShopOrder,
  ) {
    const { shippingAddress, shippingAddressRegion } = order;

    if (!shippingAddressRegion || !shippingAddress) return;

    const addressInput = this.addressService.getAddressInputFromShopAddress(shippingAddress);

    patchState({ addressInput });
    dispatch(new RegionSet(shippingAddressRegion));
  }

  @Action(ChannelSetActive)
  onChannelSetActive(
    { patchState }: StateContext<IRegionStateModel>,
    { activeChannel }: ChannelSetActive,
  ) {
    const {
      channel: { regions: channelRegions },
    } = activeChannel;

    patchState({
      ...defaults,
      channelRegions,
      defaultCoordinates: this.shopConfig.googleMaps.defaultCoordinates,
      deliveryMarkers: this.getGoogleMarkers(channelRegions),
    });
  }

  @Action(RegionSetFromAddressInput)
  setFromAddressInput(
    { patchState, dispatch }: StateContext<IRegionStateModel>,
    { addressInput }: RegionSetFromAddressInput,
  ) {
    patchState({ addressInput });

    return this.shopRegionService.regionFromAddressInput({ address: addressInput }).pipe(
      tap((region) => {
        if (!region) return;
        dispatch(new RegionSet(region));
      }),
    );
  }

  @Action(RegionOpenOverlay)
  openOverlay(
    { patchState }: StateContext<IRegionStateModel>,
    { productContext }: RegionOpenOverlay,
  ) {
    patchState({
      openOverlay: true,
      mode: productContext ? 'add-to-cart' : 'normal',
      ...(productContext && { productContext }),
    });
  }

  @Action(RegionCloseOverlay)
  closeOverlay({ patchState }: StateContext<IRegionStateModel>) {
    patchState({ openOverlay: false });
  }

  @Action(RegionUpdateCoordinates)
  updateCoordinates(
    { patchState, getState }: StateContext<IRegionStateModel>,
    { coordinates }: RegionUpdateCoordinates,
  ) {
    const { addressInput } = getState();
    if (!addressInput) return;

    patchState({
      addressInput: {
        ...addressInput,
        coordinates,
      },
    });
  }

  private getGooglePolygons(
    region: IShopGeoRegionDetail | IShopGeoRegionDetail[],
  ): google.maps.PolygonOptions[] {
    const arr = Array.isArray(region) ? region : [region];

    return (
      arr
        .map((region) => {
          return region.definitions.map((def) => {
            if (!def.boundary) return null;

            return {
              paths: [def.boundary.exterior, ...def.boundary.interior],
              ...this.mapThemeConfig.polygonTheme,
            } as google.maps.PolygonOptions;
          });
        })
        .flat()
        .filter((i): i is google.maps.PolygonOptions => !!i) ?? []
    );
  }

  private getGoogleMarkers(
    region: IShopGeoRegionDetail | IShopGeoRegionDetail[],
  ): google.maps.MarkerOptions[] {
    const arr = Array.isArray(region) ? region : [region];

    return (
      arr
        .map((region) => {
          return region.definitions.map((def) => {
            if (!def.center) return null;

            const icon =
              this.shopConfig.googleMaps.iconMap[region.code] ??
              this.mapThemeConfig.markerIconRegion;

            return {
              position: def.center,
              icon: {
                url: icon.url,
                scaledSize: { ...icon.size },
              },
            } as google.maps.MarkerOptions;
          });
        })
        .flat()
        .filter((i): i is google.maps.MarkerOptions => !!i) ??
      [] ??
      []
    );
  }
}
